import { api } from '@/lib/api'
import type {
  AiGatewayMemberSnapshot,
  AiGatewayRoleLimit,
  AiGatewayRoleLimitsResponse,
  ApiKeysResponse,
  AuditLogsResponse,
  CreateApiKeyResponse,
  CreateInvitationResponse,
  InvitationsResponse,
  MembersResponse,
  RoleListResponse,
  RolePermissionsResponse,
  RolesResponse,
} from './types'

export const teamApi = {
  members: () => api.get<MembersResponse>('/api/tenant/members'),
  roles: () => api.get<RolesResponse>('/api/tenant/roles'),
  setRole: (id: number, roleId: number) =>
    api.patch<{ message?: string }>(`/api/tenant/members/${id}/role`, { roleId }),
  activate: (id: number) => api.post<{ message?: string }>(`/api/tenant/members/${id}/activate`),
  deactivate: (id: number) => api.post<{ message?: string }>(`/api/tenant/members/${id}/deactivate`),
  remove: (id: number) => api.del<{ message?: string }>(`/api/tenant/members/${id}`),

  rolesFull: () => api.get<RoleListResponse>('/api/tenant/roles'),
  rolePermissions: () => api.get<RolePermissionsResponse>('/api/tenant/roles/permissions'),
  createRole: (body: { name: string; description?: string; permissions?: string[] }) =>
    api.post<{ role: { id: number } }>('/api/tenant/roles', body),
  updateRole: (id: number, body: { name?: string; description?: string }) =>
    api.put<{ message?: string }>(`/api/tenant/roles/${id}`, body),
  setRolePermissions: (id: number, permissions: string[]) =>
    api.put<{ count?: number }>(`/api/tenant/roles/${id}/permissions`, { permissions }),
  deleteRole: (id: number) => api.del<{ message?: string }>(`/api/tenant/roles/${id}`),

  invitations: () => api.get<InvitationsResponse>('/api/tenant/invitations'),
  createInvitation: (body: { email: string; roleId: number }) =>
    api.post<CreateInvitationResponse>('/api/tenant/invitations', body),
  resendInvitation: (id: string) =>
    api.post<{ acceptLink?: string }>(`/api/tenant/invitations/${id}/resend`),
  revokeInvitation: (id: string) =>
    api.del<{ message?: string }>(`/api/tenant/invitations/${id}`),

  apiKeys: () => api.get<ApiKeysResponse>('/api/tenant/api-keys'),
  createApiKey: (body: { name: string; permissions?: string[] }) =>
    api.post<CreateApiKeyResponse>('/api/tenant/api-keys', body),
  deleteApiKey: (id: number) =>
    api.del<{ message?: string }>(`/api/tenant/api-keys/${id}`),

  auditLogs: (params: {
    search?: string
    action?: string
    resource_type?: string
    limit?: number
    offset?: number
  } = {}) =>
    api.get<AuditLogsResponse>('/api/tenant/audit-logs', {
      query: {
        search: params.search ?? '',
        action: params.action ?? '',
        resource_type: params.resource_type ?? '',
        limit: params.limit ?? 50,
        offset: params.offset ?? 0,
      },
    }),

  deleteAuditLogs: (body: { ids?: (number | string)[]; olderThan?: string; all?: boolean }) =>
    api.del<{ ok: boolean; deleted: number }>('/api/tenant/audit-logs', { body }),

  aiGatewayLimits: () => api.get<AiGatewayRoleLimitsResponse>('/api/tenant/ai-gateway/limits'),
  aiGatewayMe: () => api.get<AiGatewayMemberSnapshot>('/api/tenant/ai-gateway/me'),
  setAiGatewayLimits: (
    roleId: number,
    body: { monthlyTokenLimit: number; maxAgents: number; maxWorkflows: number },
  ) =>
    api.put<{ limits: AiGatewayRoleLimit }>(`/api/tenant/ai-gateway/limits/${roleId}`, body),
}
